import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { OrganisationInterface } from 'src/app/model/organisations/organisation-interface';
import { ProviderInterface } from 'src/app/model/user/provider-interface';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { DialogService } from 'src/app/services/dialog-service/dialog-service.service';
import { HeaderServiceService } from 'src/app/services/header-service/header-service.service';
import { UserService } from 'src/app/services/user-service/userservice';
import { UtilService } from 'src/app/services/util-service/utilService';
import { API_BASE_FILESERVICE, API_MAP } from 'src/environments/api';
import { environment } from 'src/environments/environment';
import { MinLengthErrorStateMatcher, VALIDATORS, streetValidator } from 'src/environments/validators';
import { Subscription } from 'rxjs';
import { VerifiableCredentialService } from '../../../construct-x/data-space/services/verifiable-credential.service';
import { ShaclApiService } from '../../../construct-x/data-space/services/shacl-api.service';
import { OrganisationType } from 'src/app/model/enums/organisationType';
import { HOUSE_NUMBER_REGEX, PHONE_NUMBER_REGEX, CITY_REGEX, POSTAL_CODE_REGEX } from 'src/environments/constants';


@Component({
    selector: 'app-organisation-edit',
    templateUrl: './organisation-edit.component.html',
    styleUrls: ['./organisation-edit.component.css'],
    standalone: false
})
export class OrganisationEditComponent implements OnInit {
    @Input() organisation: OrganisationInterface;

    @Output() close = new EventEmitter<boolean>();

    organisationEditForm: UntypedFormGroup;
    get domain(): AbstractControl {
        return this.organisationEditForm.get('domain');
    }
    get name(): AbstractControl {
        return this.organisationEditForm.get('name');
    }
    get type(): AbstractControl {
        return this.organisationEditForm.get('type');
    }
    get street(): AbstractControl {
        return this.organisationEditForm.get('street');
    }
    get hNr() {
        return this.organisationEditForm.get('hNr');
    }
    get postalCode(): AbstractControl {
        return this.organisationEditForm.get('postalCode');
    }
    get city(): AbstractControl {
        return this.organisationEditForm.get('city');
    }

    get phoneNumber(): AbstractControl {
        return this.organisationEditForm.get('phoneNumber');
    }

    get url() {
        return this.organisationEditForm.get('url');
    }

    get contactGroup(): UntypedFormGroup {
        return this.organisationEditForm.get('contact') as UntypedFormGroup;
    }

    get contactName(): AbstractControl {
        return this.contactGroup.get('contactName');
    }

    get contactEmail(): AbstractControl {
        return this.contactGroup.get('email');
    }

    get registrationNumber(): AbstractControl {
        return this.organisationEditForm.get('registrationNumber');
    }

    get termsAndConditions(): AbstractControl {
        return this.organisationEditForm.get('termsAndConditions');
    }

    get registrationType(): AbstractControl {
        return this.organisationEditForm.get('registrationType');
    }

    get countryCode(): AbstractControl {
        return this.organisationEditForm.get('countryCode');
    }

    readonly domainPrefix = '@';
    isDescriptionValid: boolean = true;
    isNewOrganisation: boolean = true;

    orgaNameErrorStateMatcher = new MinLengthErrorStateMatcher(160);
    private createContactSub: Subscription;
    public isLoading: boolean = false;

    constructor(
        private dialogService: DialogService,
        private userService: UserService,
        private utilService: UtilService,
        private organisationService: OrganisationService,
        private headerService: HeaderServiceService,
        private formBuilder: UntypedFormBuilder,
        private cd: ChangeDetectorRef,
        private verifiableCredentialService: VerifiableCredentialService,
        private shaclApiService: ShaclApiService
    ) { }

    ngOnInit(): void {
        if (this.organisation.organisationId != null) {
            this.isNewOrganisation = false;
        }
        this.initializeForm(this.organisation);
        this.setupContactToggle();
        this.setupTypeChanges();
    }

    private initializeForm(organisation: OrganisationInterface) {
        this.organisationEditForm = this.formBuilder.group({
            name: [organisation.organisationName, [Validators.required, Validators.maxLength(160), Validators.minLength(2)]],
            type: [this.normalizeOrganisationTypes(organisation.organisationType), Validators.required],
            street: [organisation.address.street, [Validators.required, streetValidator]],
            hNr: [organisation.address.streetAdditional, [Validators.required, Validators.pattern(HOUSE_NUMBER_REGEX), Validators.maxLength(15)]],
            postalCode: [organisation.address.postalCode, [Validators.required, Validators.pattern(POSTAL_CODE_REGEX)]],
            city: [organisation.address.city, [Validators.required, Validators.pattern(CITY_REGEX)]],
            phoneNumber: [organisation.phoneNumber, Validators.pattern(PHONE_NUMBER_REGEX)],
            domain: [organisation.domain, Validators.required],
            url: [organisation.website, VALIDATORS.website],
            createContact: [false], // Toggle for contact creation
            contact: this.formBuilder.group({
                contactName: [''],
                phoneNumber: [''],
                mobilePhoneNumber: [''],
                email: [''],
                website: [''],
                gender: ['']
            }),
            registrationNumber: [''],
            registrationType: [''],
            countryCode: [''],
            termsAndConditions: [false]
        });
    }

    changeDomainToName(organisationName: string): void {
        let domainString: string = '';
        let format = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?äÄöÖüÜ]+/;
        for (let letter of organisationName) {
            if (format.test(letter)) {
                domainString = domainString.concat('_');
            } else {
                domainString = domainString.concat(letter);
            }
        }
        const domainField = this.domain;
        domainField.setValue(domainString);
        domainField.setErrors(null);
    }

    isUserPlatformAdmin(): boolean {
        return this.userService.isCurrentUserPlatformAdmin();
    }

    onDescriptionValidityChange(descriptionValid: boolean): void {
        this.isDescriptionValid = descriptionValid;
    }

    private getFormFieldValue(fieldName: string): any {
        return this.organisationEditForm.get(fieldName).value;
    }

    onOrganisationConfirm(): void {
        const organisationName = this.getFormFieldValue('name');
        if (
            !UtilService.areStringsValid(
                organisationName,
                this.organisation.description
            )
        ) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte stellen Sie sicher, dass der Name und die Beschreibung der Organisation nicht zu lang sind.'
            );
            return;
        }

        if (this.organisation.fileId == null) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte wählen Sie ein Logo für die Organisation.'
            );
            return;
        }

        this.dialogService
            .openConfirmDialog(
                'Möchtest du diese Organisation wirklich anlegen?',
                'Nein',
                'Ja',
                ''
            )
            .subscribe((result) => {
                if (result) {
                    this.isLoading = true;
                    this.cd.detectChanges();
                    this.applyChangesToOrganisation();
                    const contactData = this.organisationEditForm.get('createContact').value
                        ? this.organisationEditForm.get('contact').value
                        : null;

                    const formData = this.organisationEditForm.value;
                    const isDataspaceParticipant = this.normalizeOrganisationTypes(formData.type).includes(OrganisationType.DATASPACE_PARTICIPANT);

                    this.organisationService.postItem(this.organisation).then(
                        async (org) => {
                            if (isDataspaceParticipant) {
                                // Logic for Dataspace Participant VC generation
                                const regTypeKey = formData.registrationType; // e.g. 'gx:vatID'
                                const registrationNumberObj = {};
                                if (regTypeKey) {
                                    registrationNumberObj[regTypeKey] = formData.registrationNumber;
                                }

                                const vcData = {
                                    'gx:legalName': org.organisationName,
                                    'gx:legalRegistrationNumber': registrationNumberObj,
                                    'gx:headquarterAddress': {
                                        'gx:countrySubdivisionCode': formData.postalCode + '-' + formData.city, // simplified mapping
                                        'gx:addressCountryCode': formData.countryCode,
                                        'gx:streetAddress': formData.street + ' ' + formData.hNr,
                                        'gx:postalCode': formData.postalCode,
                                        'gx:locality': formData.city
                                    }
                                };
                                // Note: Schema and Shape would ideally be dynamic.
                                // Using a mock shape or one fetched from service would be better, but constructing a basic one here
                                const shape = {
                                    targetClass: 'gx:LegalParticipant',
                                    id: 'https://registry.lab.gaia-x.eu/development/api/trusted-shape-registry/v1/shapes/jsonld/trustframework#LegalParticipantShape'
                                } as any;

                                const vcResult = this.verifiableCredentialService.generateCredential(vcData, shape);

                                if (vcResult.success) {
                                    this.verifiableCredentialService.saveToBackend({
                                        name: org.organisationName,
                                        description: org.description || 'Dataspace Participant Credential',
                                        targetClass: 'gx:LegalParticipant',
                                        shapeId: shape.id,
                                        formData: vcData,
                                        orgId: org.organisationId
                                    }).subscribe(
                                        res => console.log('VC Saved', res),
                                        err => console.error('Error saving VC', err)
                                    );
                                } else {
                                    console.error('Failed to generate VC', vcResult.error);
                                    this.dialogService.openDialog('Fehler', 'Verifiable Credential konnte nicht erstellt werden: ' + vcResult.error);
                                }
                            }

                            if (contactData) {
                                try {
                                    await this.organisationService
                                        .createContactForNewOrganisation(org.organisationId, contactData)
                                        .toPromise();
                                    const updatedOrg = await this.organisationService.reload(org.organisationId);
                                    await this.organisationService.setActiveOrganisation(updatedOrg);
                                } catch (err) {
                                    this.isLoading = false;
                                    this.cd.detectChanges();
                                    console.warn('Kontakt konnte nicht angelegt werden', err);
                                    this.dialogService.openDialog('Hinweis', 'Kontakt konnte nicht angelegt werden.');
                                }
                            } else {
                                await this.organisationService.setActiveOrganisation(org.organisationId);
                            }
                            this.isLoading = false;
                            this.cd.detectChanges();
                            await this.organisationService.loadOrganisationsForUser();

                            this.dialogService.openDialog(
                                'Organisation erstellt',
                                'Die neue Organisation ' +
                                org.organisationName +
                                ' wurde erfolgreich erstellt.'
                            );
                            this.organisationEditForm.reset();

                            let image = org.fileId
                                ? API_BASE_FILESERVICE +
                                API_MAP['files']['GET'].replace(
                                    ':fileId',
                                    org.fileId
                                )
                                : environment.defaultPictureOrganisationProducts;
                            let updatedMembership: ProviderInterface = {
                                name: org.organisationName,
                                img: image,
                                organisationId: org.organisationId
                            };

                            this.headerService.setActiveProvider(
                                updatedMembership
                            );
                        },
                        (error) => {
                            this.isLoading = false;
                            this.cd.detectChanges();
                            this.dialogService.openDialog(
                                'Fehler beim Erstellen',
                                'Die Organisation konnte nicht erstellt werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.'
                            );
                        }
                    );
                    this.userService.updateAuthorisation();
                }
            });
    }

    private setupTypeChanges() {
        this.type.valueChanges.subscribe(value => {
            const selectedTypes = this.normalizeOrganisationTypes(value);
            this.updateDataspaceValidators(selectedTypes);
        });

        this.updateDataspaceValidators(this.normalizeOrganisationTypes(this.type.value));
    }

    private updateDataspaceValidators(selectedTypes: OrganisationType[]): void {
        if (selectedTypes.includes(OrganisationType.DATASPACE_PARTICIPANT)) {
            this.registrationNumber.setValidators([Validators.required]);
            this.registrationType.setValidators([Validators.required]);
            this.countryCode.setValidators([
                Validators.required,
                Validators.maxLength(2),
                Validators.minLength(2)
            ]);
            this.termsAndConditions.setValidators([Validators.requiredTrue]);
        } else {
            this.registrationNumber.clearValidators();
            this.registrationType.clearValidators();
            this.countryCode.clearValidators();
            this.termsAndConditions.clearValidators();
        }
        this.registrationNumber.updateValueAndValidity();
        this.registrationType.updateValueAndValidity();
        this.countryCode.updateValueAndValidity();
        this.termsAndConditions.updateValueAndValidity();
    }

    private applyChangesToOrganisation(): void {
        this.organisation.organisationName = this.getFormFieldValue('name');
        this.organisation.organisationType = this.normalizeOrganisationTypes(this.getFormFieldValue('type'));
        this.organisation.address = {
            street: this.getFormFieldValue('street'),
            streetAdditional: this.getFormFieldValue('hNr'),
            postalCode: this.getFormFieldValue('postalCode'),
            city: this.getFormFieldValue('city')
        };
        this.organisation.contacts = [];
        this.organisation.domain = this.getFormFieldValue('domain');
        this.organisation.website = this.getFormFieldValue('url');
        this.organisation.phoneNumber = this.getFormFieldValue('phoneNumber');
        if (
            this.utilService.isStringNullOrEmpty(this.organisation.description)
        ) {
            this.organisation.description = '';
        }
    }

    onAbort(): void {
        if (this.isNewOrganisation) {
            this.organisationEditForm.reset();
        }
        this.close.emit(false);
    }

    private setupContactToggle() {
        const createContactControl = this.organisationEditForm.get('createContact');
        const contactGroup = this.organisationEditForm.get('contact');

        this.createContactSub = createContactControl.valueChanges.subscribe((create) => {
            if (create) {
                contactGroup.get('contactName').setValidators([Validators.required]);
                contactGroup.get('email').setValidators([Validators.required, Validators.email]);
            } else {
                contactGroup.get('contactName').clearValidators();
                contactGroup.get('email').clearValidators();
                contactGroup.reset();
            }
            contactGroup.get('contactName').updateValueAndValidity();
            contactGroup.get('email').updateValueAndValidity();
        });
    }

    checkPhonenumber(): boolean {
        const phone = this.phoneNumber.value?.trim();

        if (!phone) {
            return true;
        }

        const isValid = this.utilService.validateInternationalPhone(phone);

        if (!isValid) {
            this.phoneNumber.setErrors({ invalid: true });
            return false;
        }
        return true;
    }

    ngOnDestroy(): void {
        this.createContactSub?.unsubscribe();
    }

    trimField(controlName: string, group: 'contact' | null = null) {
        const control = group
            ? this.organisationEditForm.get(`${group}.${controlName}`)
            : this.organisationEditForm.get(controlName);

        if (control) {
            control.setValue(control.value?.trim());
        }
    }

    private normalizeOrganisationTypes(value: any): OrganisationType[] {
        if (!value) {
            return [];
        }
        return (Array.isArray(value) ? value : [value]).filter(Boolean) as OrganisationType[];
    }

}
